import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ServicePageLayout from '../../components/Layout/ServicePageLayout';
import { FaPalette, FaMobile, FaDesktop, FaUsers, FaEye, FaBrain } from 'react-icons/fa';
import { SiFigma, SiAdobexd, SiSketch, SiInvision } from 'react-icons/si';
import { MdEmail, MdPhone, MdPerson, MdMessage } from 'react-icons/md';
import { submitToGoogleSheet } from '../../utils/helpers';
import classes from './ServicePage.module.scss';
import contactClasses from '../../components/Contact/Contact.module.scss';
import FAQ from '../../components/FAQ/FAQ';
import uiUxHeroImg from '../../assets/ui_ux_service.png';
import artistNextDoorPortfolio from '../../assets/artistnextdoor_portfolio.png';
import palmistryPortfolio from '../../assets/palmistry_portfolio.png';
import zazidatingPortfolio from '../../assets/zazidating_portfolio.png';
import uiUxMobile from '../../assets/ui_ux_mobile.png';

const UIUXDesign = () => {
  const { projects } = useSelector((state) => state.portfolio);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    message: '',
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitToGoogleSheet(formData);
      if (typeof window.gtag_report_conversion === 'function') {
        window.gtag_report_conversion();
      }
      alert('Thank you! We will get back to you soon.');
      setFormData({ name: '', email: '', phoneNumber: '', message: '' });
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const designProjects = [
    {
      id: 1,
      title: 'ARTIST NEXT DOOR',
      subtitle: 'UI/UX Design & Branding',
      description: 'Complete design system and user experience for an artist booking platform. Crafted intuitive flows for both artists and clients.',
      image: artistNextDoorPortfolio,
      category: 'UI/UX',
      link: '/services/ui-ux-design',
      bgGradient: 'linear-gradient(135deg, rgb(133, 89, 200) 0%, rgb(52, 29, 89) 100%)'
    },
    {
      id: 2,
      title: 'PALMISTRY APP',
      subtitle: 'Mobile App Design',
      description: 'Elegant and mystical UI design for a palmistry app with immersive visuals, smooth onboarding, and engaging user experience.',
      image: palmistryPortfolio,
      category: 'UI/UX',
      link: '/services/ui-ux-design',
      bgGradient: 'linear-gradient(135deg, rgb(45, 80, 120) 0%, rgb(20, 40, 70) 100%)'
    },
    {
      id: 3,
      title: 'ZAZI DATING',
      subtitle: 'Dating App Design',
      description: 'Modern, vibrant UI design for a dating application with focus on user engagement, matching flows, and profile interactions.',
      image: zazidatingPortfolio,
      category: 'UI/UX',
      link: '/services/ui-ux-design',
      bgGradient: 'linear-gradient(135deg, rgb(200, 80, 120) 0%, rgb(120, 30, 60) 100%)'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % designProjects.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + designProjects.length) % designProjects.length);
  };

  const technologies = [
    { name: 'Figma', icon: <SiFigma />, description: 'Design and prototyping' },
    { name: 'Adobe XD', icon: <SiAdobexd />, description: 'User experience design' },
    { name: 'Sketch', icon: <SiSketch />, description: 'Interface design' },
    { name: 'InVision', icon: <SiInvision />, description: 'Interactive prototypes' },
  ];

  const features = [
    {
      title: 'User Research',
      description: 'Deep understanding of your users through research, interviews, and data-driven analysis.',
      image: uiUxMobile
    },
    {
      title: 'Visual Design',
      description: 'Beautiful, modern designs that reflect your brand identity and engage users.',
      image: uiUxHeroImg
    },
    {
      title: 'User Experience',
      description: 'Intuitive user flows and interactions that delight users and drive conversions.',
      image: uiUxMobile
    },
    {
      title: 'Responsive Design',
      description: 'Designs that work perfectly across all devices and screen sizes.',
      image: uiUxHeroImg
    }
  ];

  const faqs = [
    {
      question: 'What is the difference between UI and UX design?',
      answer: 'UX (User Experience) focuses on how a product feels — user flows, information architecture, and usability. UI (User Interface) focuses on how it looks — colors, typography, buttons, and visual layout. We handle both to create designs that are beautiful and easy to use.'
    },
    {
      question: 'How long does the design process take?',
      answer: 'A typical design project takes 3-6 weeks depending on complexity. This includes user research (1 week), wireframing (1 week), visual design (2-3 weeks), and prototyping with revisions (1 week). We work in iterative cycles with your feedback at each stage.'
    },
    {
      question: 'Do you conduct user research and testing?',
      answer: 'Yes. We conduct user interviews, competitor analysis, usability testing, and A/B testing. Understanding real user behavior ensures our designs solve actual problems and drive measurable improvements in engagement and conversions.'
    },
    {
      question: 'What deliverables will I receive?',
      answer: 'You receive wireframes, high-fidelity mockups, interactive prototypes, design system documentation, style guides, and developer-ready assets. All files are organized in Figma with proper components, variants, and responsive layouts.'
    },
    {
      question: 'Can you redesign my existing product?',
      answer: 'Absolutely. We audit your current design, identify pain points through analytics and user feedback, then create an improved version that retains brand identity while significantly enhancing usability and visual appeal.'
    },
    {
      question: 'Do you design for both web and mobile?',
      answer: 'Yes. We design responsive web interfaces, native iOS and Android apps, and cross-platform solutions. Every design follows platform-specific guidelines (Material Design, Human Interface Guidelines) while maintaining brand consistency.'
    },
  ];

  const process = [
    { step: '01', title: 'User Research', description: 'Understanding user needs, behaviors, and pain points' },
    { step: '02', title: 'Information Architecture', description: 'Organizing content and defining user flows' },
    { step: '03', title: 'Wireframing', description: 'Creating low-fidelity layouts and structures' },
    { step: '04', title: 'Visual Design', description: 'Designing high-fidelity mockups and interfaces' },
    { step: '05', title: 'Prototyping', description: 'Creating interactive prototypes for testing' },
    { step: '06', title: 'Testing & Iteration', description: 'User testing and design refinements' },
  ];

  return (
    <ServicePageLayout>
      <div className={classes.servicePageContainer}>
        {/* Hero Section */}
        <section className={classes.hero}>
          <div className={classes.container}>
            <div className={classes.heroContent}>
              <div className={classes.heroHighlight}>
                <div className={classes.heroText}>
                  <h1 className={classes.heroTitle}>
                    User Interface & <span className={classes.gradient}>Experience Design</span>
                  </h1>
                  <p className={classes.heroSubtitle}>
                    Create beautiful, intuitive designs that users love. From wireframes to pixel-perfect
                    interfaces, we craft experiences that engage users and drive conversions.
                  </p>
                  <div className={classes.heroCTA}>
                    <button
                      className={classes.exploreButton}
                      onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Explore Services
                    </button>
                    <button
                      className={classes.quoteButton}
                      onClick={() => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Get A Quote
                    </button>
                  </div>
                  <div className={classes.heroStats}>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>{designProjects.length}+</span>
                      <span className={classes.statLabel}>Design Projects</span>
                    </div>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>95%</span>
                      <span className={classes.statLabel}>User Satisfaction</span>
                    </div>
                    <div className={classes.stat}>
                      <span className={classes.statNumber}>40%</span>
                      <span className={classes.statLabel}>Conversion Increase</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={classes.heroVisual}>
                <div className={classes.phoneContainer}>
                  <img
                    src={uiUxHeroImg}
                    alt="UI/UX Design"
                    className={classes.phoneImage}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className={classes.features}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Why Choose Our <span className={classes.gradient}>Design Services</span>
              </h2>
            </div>
            <div className={classes.featuresGrid}>
              {features.map((feature, index) => (
                <div key={index} className={classes.featureCard}>
                  <div className={classes.featureImageContainer}>
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className={classes.featureImage}
                    />
                  </div>
                  <h3 className={classes.featureTitle}>{feature.title}</h3>
                  <p className={classes.featureDescription}>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies Section */}
        <section className={classes.technologies}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Design <span className={classes.gradient}>Tools & Software</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                Professional design tools for creating world-class user experiences
              </p>
            </div>
            <div className={classes.techGrid}>
              {technologies.map((tech, index) => (
                <div key={index} className={classes.techCard}>
                  <div className={classes.techIcon}>{tech.icon}</div>
                  <h3 className={classes.techName}>{tech.name}</h3>
                  <p className={classes.techDescription}>{tech.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Projects Section - Carousel */}
        <section className={classes.portfolioSection}>
          <div className={classes.portfolioContainer}>
            <div className={classes.portfolioHeader}>
              <h2 className={classes.portfolioTitle}>
                Our Design <span className={classes.gradient}>Projects</span>
              </h2>
            </div>

            <div className={classes.carousel}>
              <div className={classes.slideContainer}>
                <div
                  className={classes.slides}
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {designProjects.map((project, index) => (
                    <div
                      key={project.id}
                      className={classes.slide}
                      style={{ background: project.bgGradient }}
                    >
                      <div className={classes.slideContent}>
                        <div className={classes.slideTop}>
                          <div className={classes.imageContainer}>
                            <img src={project.image} alt={project.title} />
                          </div>
                        </div>

                        <div className={classes.slideBottom}>
                          <div className={classes.slideHeader}>
                            <h3 className={classes.projectTitle}>{project.title}</h3>
                            <span className={classes.categoryBadge}>{project.category}</span>
                          </div>

                          <h4 className={classes.projectSubtitle}>{project.subtitle}</h4>

                          <p className={classes.projectDescription}>{project.description}</p>

                          <a href={project.link} className={classes.learnMoreBtn}>
                            <span>Learn More</span>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={classes.navigation}>
                <button
                  className={`${classes.navBtn} ${classes.prevBtn}`}
                  onClick={prevSlide}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <button
                  className={`${classes.navBtn} ${classes.nextBtn}`}
                  onClick={nextSlide}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              <div className={classes.indicators}>
                {designProjects.map((_, index) => (
                  <button
                    key={index}
                    className={`${classes.indicator} ${index === currentSlide ? classes.active : ''}`}
                    onClick={() => setCurrentSlide(index)}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className={classes.process}>
          <div className={classes.container}>
            <div className={classes.sectionHeader}>
              <h2 className={classes.sectionTitle}>
                Our Design <span className={classes.gradient}>Process</span>
              </h2>
              <p className={classes.sectionSubtitle}>
                A user-centered approach to creating exceptional digital experiences
              </p>
            </div>
            <div className={classes.processGrid}>
              {process.map((step, index) => (
                <div key={index} className={classes.processStep}>
                  <div className={classes.stepNumber}>{step.step}</div>
                  <h3 className={classes.stepTitle}>{step.title}</h3>
                  <p className={classes.stepDescription}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <FAQ faqs={faqs} title="Frequently Asked" highlight="Questions" />

        {/* Contact Form Section */}
        <section id="contact-form" className={contactClasses.contact}>
          <div className={contactClasses.container}>
            <div className={contactClasses.header}>
              <span className={contactClasses.badge}>Get Your Quote</span>
              <h2 className={contactClasses.title}>
                Let's Design Your <span className={contactClasses.gradient}>User Experience</span>
              </h2>
            </div>

            <div className={contactClasses.content}>
              <form className={contactClasses.form} onSubmit={handleSubmit}>
                <div className={contactClasses.formRow}>
                  <div className={contactClasses.formGroup}>
                    <label htmlFor="name">
                      <MdPerson className={contactClasses.labelIcon} />
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your full name"
                      className={contactClasses.premiumInput}
                    />
                  </div>

                  <div className={contactClasses.formGroup}>
                    <label htmlFor="email">
                      <MdEmail className={contactClasses.labelIcon} />
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your.email@company.com"
                      className={contactClasses.premiumInput}
                    />
                  </div>
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="phoneNumber">
                    <MdPhone className={contactClasses.labelIcon} />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    placeholder="+1 (555) 123-4567"
                    className={contactClasses.premiumInput}
                  />
                </div>

                <div className={contactClasses.formGroup}>
                  <label htmlFor="message">
                    <MdMessage className={contactClasses.labelIcon} />
                    Design Project Details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us about your design vision - target audience, brand guidelines, features, timeline, and any specific design requirements..."
                    className={contactClasses.premiumTextarea}
                  ></textarea>
                </div>

                <button type="submit" className={contactClasses.submitBtn} disabled={loading}>
                  <span className={contactClasses.btnText}>
                    {loading && <span className={contactClasses.spinner} />}
                    {loading ? 'Sending...' : 'Get My Quote'}
                  </span>
                  <div className={contactClasses.btnGlow}></div>
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={classes.cta}>
          <div className={classes.container}>
            <div className={classes.ctaContent}>
              <h2 className={classes.ctaTitle}>
                Ready to Create Amazing <span className={classes.gradient}>User Experiences</span>?
              </h2>
              <p className={classes.ctaSubtitle}>
                Let's design something beautiful that your users will love to use
              </p>
              <a href="#contact-form" className={classes.ctaButton}>Start Design Project</a>
            </div>
          </div>
        </section>
      </div>
    </ServicePageLayout>
  );
};

export default UIUXDesign;
